<script lang="ts">
  import { EVOLVE_SCREEN } from '../../game/constants/hud';
  import { evolutionChoices } from '../../game/constants/ship-tree';
  import { shipName } from '../../game/constants/ships';
  import { resolvedUpgradeForSlot, upgradeLabel } from '../../game/constants/upgrades';
  import { sharedAssets } from '../../assets/asset-store';
  import { atlasSignal } from '../../assets/atlas-signal.svelte';
  import { socketState } from '../socket-state.svelte';
  import MetalFrame from './MetalFrame.svelte';
  import ShipArt from '../ShipArt.svelte';
  import { forgetMeasurements, widestRatio } from './text-fit';
  import { wornSkin } from './worn-skin';

  interface Props {
    shipId: number;
    /** Wire team value, so the portraits are the player's own colour. */
    team: number;
    evolutionAbilities?: Readonly<Record<number, readonly number[]>>;
    onchoose: (path: number) => void;
    onclose: () => void;
  }

  const { shipId, team, evolutionAbilities = {}, onchoose, onclose }: Props = $props();

  const {
    panel,
    dim,
    heading,
    columnCentreFloor,
    portrait,
    shipName: nameText,
    ability,
    choose,
    key,
    close,
  } = EVOLVE_SCREEN;

  /** The close button's top edge is 25px up from the panel's bottom, so it hangs below it. */
  const closeBottom = -(close.height + close.centreYFromBottom);

  const choices = $derived(evolutionChoices(shipId));

  const columnWidth = $derived(100 / (choices.length + 1));

  const GUTTER = 12;

  const FALLBACK_FIT = { ability: 11, name: 8 };

  let fontReady = $state(false);
  if (typeof document !== 'undefined') {
    void document.fonts.ready.then(() => {
      forgetMeasurements();
      fontReady = true;
    });
  }

  const abilityFit = $derived.by(() => {
    void fontReady;
    return widestRatio(
      choices.flatMap((choice) => abilities(choice.shipId)),
      FALLBACK_FIT.ability,
    );
  });

  const nameFit = $derived.by(() => {
    void fontReady;
    return widestRatio(
      choices.map((choice) => shipName(choice.shipId)),
      FALLBACK_FIT.name,
    );
  });

  function fitted(max: number, ratio: number): string {
    return `min(${String(max)}px, calc((${String(columnWidth)}cqw - ${String(GUTTER)}px) / ${String(ratio)}))`;
  }

  /** Q, W, E select the columns, left to right. */
  const KEYS = ['Q', 'W', 'E'] as const;

  let root: HTMLDivElement | undefined = $state();

  // The overlay swallows keys so the game does not act on them (see the markup), which only works
  // while it holds focus.
  $effect(() => {
    root?.focus();
  });

  function abilities(candidate: number): readonly string[] {
    const resolved = evolutionAbilities[candidate] ?? null;
    return [0, 1, 2].map(
      (slot) => upgradeLabel(resolvedUpgradeForSlot(candidate, slot, resolved)).name,
    );
  }

  function skinCss(candidate: number) {
    void atlasSignal.generation;
    const worn = wornSkin(socketState.products, socketState.equipped('skin', candidate), team);
    if (worn === null) return null;
    return sharedAssets.cosmeticCss(worn.imagePath, worn.shift, portrait.size);
  }

  /** Column centres, as a percentage of the panel width: 25/50/75 for three, 50 for one. */
  function columnPercent(index: number, count: number): number {
    return ((index + 1) / (count + 1)) * 100;
  }

  function onkeydown(event: KeyboardEvent): void {
    // Stopped either way: while this is open the game must not see Q/W/E as upgrade purchases, R as
    // a second toggle, or Escape as a suicide.
    event.stopPropagation();

    if (event.code === 'Escape' || event.code === 'KeyR') {
      event.preventDefault();
      onclose();
      return;
    }

    const column = KEYS.findIndex((letter) => event.code === `Key${letter}`);
    const choice = column < 0 ? undefined : choices[column];
    if (choice !== undefined) {
      event.preventDefault();
      onchoose(choice.path);
    }
  }
</script>

<!--
  The ship-evolution screen: one column per ship the player can become, each with its portrait, its
  name and the three abilities it would bring.

  It covers the whole viewport and takes pointer events, which is how a click on it does not reach
  the canvas and fire the ship's weapon. `keydown` is stopped for the same reason: the game's
  bindings live on `window`, so an unstopped Q would both evolve and buy an upgrade.

  `pointerdown` on the backdrop is prevented so clicking outside the buttons does not blur the
  overlay and hand the keys back to the game.
-->
<div
  class="evolve"
  bind:this={root}
  role="dialog"
  aria-modal="true"
  aria-label="Upgrade ship"
  data-testid="evolve-screen"
  tabindex="-1"
  {onkeydown}
  onpointerdown={(event) => {
    if (event.target === event.currentTarget) event.preventDefault();
  }}
>
  <div class="evolve__dim" style:background-color={dim.color} style:opacity={dim.alpha}></div>

  <div
    class="evolve__panel"
    style:width="min({panel.maxWidth}px, 100% - {EVOLVE_SCREEN.inset * 2}px)"
    style:height="min({panel.maxHeight}px, 100% - {EVOLVE_SCREEN.inset * 2}px)"
  >
    <MetalFrame />

    <h2
      class="hud-text evolve__heading"
      style:top="{heading.baselineFromTop}px"
      style:font-size="{heading.font}px"
      style:color={heading.color}
    >
      {heading.text}
    </h2>

    {#each choices as choice, index (choice.path)}
      {@const skin = skinCss(choice.shipId)}
      <div
        class="evolve__column"
        data-testid="evolve-column"
        style:left="{columnPercent(index, choices.length)}%"
        style:top="max(50%, {columnCentreFloor}px)"
        style:width="{columnWidth}%"
      >
        <p
          class="hud-text evolve__wide"
          style:top="{nameText.baselineFromCentre}px"
          style:font-size={fitted(nameText.font, nameFit)}
          style:color={nameText.color}
        >
          {shipName(choice.shipId)}
        </p>

        <span
          class="evolve__portrait"
          style:top="{portrait.topFromCentre}px"
          style:left="calc(50% - {portrait.size / 2}px)"
          style:width="{portrait.size}px"
          style:height="{portrait.size}px"
        >
          <ShipArt {team} shipId={choice.shipId} size={portrait.size} />
          {#if skin !== null}
            <span
              class="evolve__skin"
              style:background-image="url({skin.url})"
              style:background-size={skin.backgroundSize}
              style:background-position={skin.backgroundPosition}
              style:width="{skin.width}px"
              style:height="{skin.height}px"
            ></span>
          {/if}
        </span>

        <ul class="evolve__abilities">
          {#each abilities(choice.shipId) as abilityName, line (line)}
            <li
              class="hud-text evolve__wide wis-tint"
              style:top="{ability.firstBaselineFromCentre + line * ability.lineHeight}px"
              style:font-size={fitted(ability.font, abilityFit)}
              style:color={ability.color}
            >
              {abilityName}
            </li>
          {/each}
        </ul>

        <button
          class="wis-btn evolve__choose"
          type="button"
          style:font-size="{choose.font}px"
          style:top="{choose.topFromCentre}px"
          style:left="calc(50% - {choose.width / 2}px)"
          style:width="{choose.width}px"
          style:height="{choose.height}px"
          aria-label="Evolve into {shipName(choice.shipId)} ({KEYS[index] ?? ''})"
          data-testid="evolve-choice-{choice.path}"
          onclick={() => {
            onchoose(choice.path);
          }}>{choose.label}</button
        >

        {#if KEYS[index] !== undefined}
          <p
            class="hud-text evolve__wide"
            style:top="{key.baselineFromCentre}px"
            style:font-size="{key.font}px"
            style:color={key.color}
          >
            Press {KEYS[index]}
          </p>
        {/if}
      </div>
    {/each}

    <button
      class="wis-btn evolve__close"
      type="button"
      style:font-size="{close.font}px"
      style:bottom="{closeBottom}px"
      style:left="{((1 - close.widthFraction) / 2) * 100}%"
      style:width="{close.widthFraction * 100}%"
      style:height="{close.height}px"
      data-testid="evolve-close"
      onclick={onclose}>{close.label}</button
    >
  </div>
</div>

<style>
  /* Every class is prefixed: `.panel` alone collides with the pre-game shell's in `app.css`. */
  .evolve {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    pointer-events: auto;
  }

  .evolve:focus {
    outline: none;
  }

  .evolve__dim {
    position: absolute;
    inset: 0;
  }

  .evolve__panel {
    position: relative;
    container-type: inline-size;
  }

  .evolve__heading {
    left: 0;
    width: 100%;
    text-align: center;
    font-weight: normal;
  }

  .evolve__column {
    position: absolute;
    transform: translateX(-50%);
  }

  .evolve__wide {
    left: 0;
    width: 100%;
    text-align: center;
    /* Shrunk to fit rather than wrapped: a second line would land on the next ability's row. */
    white-space: nowrap;
  }

  .evolve__choose,
  .evolve__close {
    color: #f2f2f2;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .evolve__abilities {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .evolve__portrait {
    position: absolute;
    display: grid;
    place-items: center;
    filter: drop-shadow(0 10px 14px rgb(0 0 0 / 55%)) drop-shadow(0 2px 4px rgb(0 0 0 / 45%));
  }

  .evolve__skin {
    position: absolute;
    top: 0;
    left: 0;
    background-repeat: no-repeat;
  }

  .evolve__choose,
  .evolve__close {
    position: absolute;
    padding: 0;
  }
</style>
